import { useCallback, useEffect, useRef, useState } from 'react'
import { OFFER_COUNTRY_TYPE, STEPS } from '../useReservation'
import { reservationApi } from '../reservationApi'
import { mapUddsCheckoutRecoveryCapture } from './mapUddsCheckoutRecoveryCapture'
import { readUddsCaptureContext } from './readUddsCaptureContext'

const CAPTURE_STEPS = [
    STEPS.INSURANCE,
    STEPS.PARKING,
    STEPS.PAYMENT,
]

const serializeCapture = (reservation, context) => {
    if (
        !context ||
        reservation.loading ||
        reservation.reservationLoading ||
        reservation.offerCountryType !== OFFER_COUNTRY_TYPE.FOREIGN ||
        !CAPTURE_STEPS.includes(reservation.currentStep)
    ) {
        return null
    }

    const { form } = reservation
    const childrenCount = context.dzieci
        ? context.dzieci.split(',').length
        : 0

    if (
        !form ||
        !Array.isArray(form.adults) ||
        !Array.isArray(form.children) ||
        form.adults.length !== context.dorosli ||
        form.children.length !== childrenCount
    ) {
        return null
    }

    return JSON.stringify(
        mapUddsCheckoutRecoveryCapture(reservation, context),
    )
}

export const useCaptureUddsCheckoutRecovery = (
    reservation,
    api = reservationApi,
) => {
    const [context] = useState(() =>
        readUddsCaptureContext(window.location, document),
    )
    const [status, setStatus] = useState('idle')

    const mounted = useRef(false)
    const running = useRef(false)
    const stopped = useRef(false)
    const desiredPayload = useRef(null)
    const confirmedPayload = useRef(null)
    const failedPayload = useRef(null)

    const serializedPayload = serializeCapture(reservation, context)

    const synchronize = useCallback(async () => {
        if (!mounted.current || running.current || stopped.current) {
            return
        }

        running.current = true

        try {
            while (mounted.current && !stopped.current) {
                const payload = desiredPayload.current

                if (payload === null) {
                    setStatus('idle')
                    return
                }

                if (payload === confirmedPayload.current) {
                    setStatus('saved')
                    return
                }

                if (payload === failedPayload.current) {
                    setStatus('error')
                    return
                }

                setStatus('saving')

                let result

                try {
                    result = await api.captureUddsRecovery(
                        JSON.parse(payload),
                    )
                } catch (error) {
                    if (!mounted.current) {
                        return
                    }

                    failedPayload.current = payload
                    setStatus('error')

                    // A newer selection can still be saved.
                    // The failed payload itself is not retried in a loop.
                    if (desiredPayload.current !== payload) {
                        continue
                    }

                    return
                }

                if (!mounted.current) {
                    return
                }

                if (!result.captured) {
                    stopped.current = true
                    setStatus('skipped')
                    return
                }

                confirmedPayload.current = payload
                failedPayload.current = null
                setStatus('saved')
            }
        } finally {
            running.current = false
        }
    }, [api])

    useEffect(() => {
        mounted.current = true

        return () => {
            mounted.current = false
            desiredPayload.current = null
        }
    }, [])

    useEffect(() => {
        desiredPayload.current = serializedPayload
        synchronize()
    }, [serializedPayload, synchronize])

    const retry = useCallback(() => {
        failedPayload.current = null
        synchronize()
    }, [synchronize])

    return {
        status,
        retry,
    }
}
